"use client";

import { type FocusEvent, type KeyboardEvent, useEffect } from "react";
import { motion } from "motion/react";
import { mergeClasses } from "@huub-web/utils";

import type { IconName } from "../../../theme";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import { useDelayedState } from "../../../hooks/useDelayedState";

import {
  optionWrapperStyle,
  optionButtonStyle,
  optionIconStyle,
  labelContainerStyle,
  labelTextStyle,
  subtitleTextStyle,
} from "./RadialMenuOption.css";
import {
  optionVariants,
  optionButtonVariants,
} from "./RadialMenuOption.motion";
import { elevations } from "../../../theme/css/elevations.css";
import { Flex } from "../../Flex";

export type RadialMenuOptionData = {
  /**
   * Unique identifier for the option
   */
  id: string;
  /**
   * Icon to display for this option
   */
  icon: IconName;
  /**
   * Label text shown on hover
   */
  label: string;
  /**
   * Short description shown below the label
   */
  subtitle?: string;
  /**
   * Callback when option is clicked
   */
  onClick?: () => void;
};

export type RadialMenuOptionProps = {
  option: RadialMenuOptionData;
  isHovered: boolean;
  /**
   * Z-index value assigned based on hover order
   */
  zIndex: number;
  /**
   * When true, the option renders at full pill width with the label
   * always visible, instead of collapsing to icon-only.
   */
  expanded?: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onClick: () => void;
  position: { x: number; y: number };
  index: number;
  total: number;
};

/**
 * Individual option button with label for the radial menu.
 * Single element that transforms from circle to pill on hover.
 */
export const RadialMenuOption = ({
  option,
  isHovered,
  zIndex,
  expanded = false,
  onHoverStart,
  onHoverEnd,
  onClick,
  position,
  index,
  total,
}: RadialMenuOptionProps) => {
  const [delayedExpanded, setDelayedExpanded] = useDelayedState(false, 500);

  useEffect(() => {
    setDelayedExpanded(expanded);
  }, [expanded, setDelayedExpanded]);

  const isActive = delayedExpanded || isHovered;

  /**
   * Handles keyboard activation of the menu option.
   * Activates on Enter or Space key press.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  /**
   * Handles focus event - shows expanded state when focused via keyboard.
   */
  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    // Only trigger hover state if focus came from keyboard navigation
    // (not from programmatic focus like auto-focus)
    if (event.target.matches(":focus-visible")) {
      onHoverStart();
    }
  };

  return (
    <motion.div
      className={optionWrapperStyle}
      style={{ zIndex, pointerEvents: "auto" }}
      variants={optionVariants}
      initial="closed"
      animate="open"
      exit="closed"
      custom={{ ...position, index, total }}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      onClick={onClick}
      role="menuitem"
      tabIndex={0}
      aria-label={option.label}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={onHoverEnd}
    >
      <motion.div
        className={mergeClasses(
          optionButtonStyle({ active: isHovered }),
          elevations.m,
        )}
        variants={optionButtonVariants}
        initial="collapsed"
        animate={isActive ? "expanded" : "collapsed"}
      >
        <div className={optionIconStyle}>
          <Icon icon={option.icon} size="s" />
        </div>
        <Flex direction="column" gap="xxs2" className={labelContainerStyle}>
          <Text
            className={labelTextStyle}
            color={isActive ? undefined : "onPrimary"}
            variant="bodyS"
          >
            {option.label}
          </Text>
          {option.subtitle && (
            <Text
              className={subtitleTextStyle}
              color={isActive ? "currentColor50" : "onPrimary"}
              variant="bodyXS"
            >
              {option.subtitle}
            </Text>
          )}
        </Flex>
      </motion.div>
    </motion.div>
  );
};

RadialMenuOption.displayName = "RadialMenuOption";
